import {
  assertFirebaseEnvironment,
  assertNoPlaceholderConfigurationValue,
  EnvironmentConfigurationError,
  environmentBannerLabel,
  parseBooleanFlag,
  parseRuntimeEnvironment,
  requireEnvironmentValue,
  type RuntimeEnvironment,
} from "../../../packages/foundation/src/index.js";
import {
  resolveWebAppCheckConfiguration,
  type WebAppCheckConfiguration,
} from "../../admin_shared/appCheck.js";
import { assertProductionWebEnvironment } from "../../admin_shared/productionEnvironment.js";

type WebEnvironment = Record<string, boolean | string | undefined>;

type OrgAdminCloudAuthTarget = {
  apiBaseUrl: string;
  appId: string;
  authAppleEnabled: boolean;
  authDomain: string;
  authGoogleEnabled: boolean;
  label: string;
  projectId: string;
  storageBucket: string;
};

const orgAdminCloudAuthTargets: Partial<Record<RuntimeEnvironment, OrgAdminCloudAuthTarget>> = {
  development: {
    apiBaseUrl: "https://us-central1-shdeed-dev.cloudfunctions.net",
    appId: "1:364487415413:web:80b8fe5970ff6882b18a3c",
    authAppleEnabled: false,
    authDomain: "shdeed-dev-org-admin.web.app",
    authGoogleEnabled: true,
    label: "Development cloud",
    projectId: "shdeed-dev",
    storageBucket: "shdeed-dev.firebasestorage.app",
  },
  staging: {
    apiBaseUrl: "https://us-central1-shdeed-staging.cloudfunctions.net",
    appId: "1:1009229768076:web:e57ad48033284d150da2a9",
    authAppleEnabled: false,
    authDomain: "shdeed-staging-org-admin.web.app",
    authGoogleEnabled: true,
    label: "Staging cloud",
    projectId: "shdeed-staging",
    storageBucket: "shdeed-staging.firebasestorage.app",
  },
};

export type OrgAdminEnvironmentConfig = {
  appCheck: WebAppCheckConfiguration;
  apiBaseUrl: string;
  bannerLabel: "DEVELOPMENT" | "STAGING" | undefined;
  environment: RuntimeEnvironment;
  authProviders: {
    apple: boolean;
    google: boolean;
  };
  featureFlags: {
    environmentDiagnostics: boolean;
  };
  firebase: {
    apiKey: string;
    appId: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    useEmulators: boolean;
  };
};

function readString(env: WebEnvironment, name: string): string | undefined {
  const value = env[name];
  return typeof value === "string" ? value : undefined;
}

export function resolveOrgAdminEnvironmentConfig(env: WebEnvironment): OrgAdminEnvironmentConfig {
  const environment = parseRuntimeEnvironment(
    readString(env, "VITE_APP_ENVIRONMENT"),
    "VITE_APP_ENVIRONMENT",
  );
  const useEmulators = parseBooleanFlag(
    readString(env, "VITE_USE_FIREBASE_EMULATOR"),
    "VITE_USE_FIREBASE_EMULATOR",
  );
  const projectId = requireEnvironmentValue(
    readString(env, "VITE_FIREBASE_PROJECT_ID"),
    "VITE_FIREBASE_PROJECT_ID",
  );

  assertFirebaseEnvironment({
    environment,
    projectId,
    useEmulators,
    variableName: "VITE_FIREBASE_PROJECT_ID",
  });

  const apiKey = requireEnvironmentValue(
    readString(env, "VITE_FIREBASE_API_KEY"),
    "VITE_FIREBASE_API_KEY",
  );
  const appId = requireEnvironmentValue(
    readString(env, "VITE_ORG_ADMIN_FIREBASE_APP_ID") ?? readString(env, "VITE_FIREBASE_APP_ID"),
    "VITE_ORG_ADMIN_FIREBASE_APP_ID",
  );

  if (environment !== "development") {
    assertNoPlaceholderConfigurationValue(apiKey, "VITE_FIREBASE_API_KEY");
    assertNoPlaceholderConfigurationValue(appId, "VITE_ORG_ADMIN_FIREBASE_APP_ID");
  }

  const apiBaseUrl = requireEnvironmentValue(
    readString(env, "VITE_API_BASE_URL"),
    "VITE_API_BASE_URL",
  );

  if (environment !== "development") {
    assertNoPlaceholderConfigurationValue(apiBaseUrl, "VITE_API_BASE_URL");
  }

  const authProviders = {
    apple: parseBooleanFlag(
      readString(env, "VITE_AUTH_APPLE_ENABLED") ?? "false",
      "VITE_AUTH_APPLE_ENABLED",
    ),
    google: parseBooleanFlag(
      readString(env, "VITE_AUTH_GOOGLE_ENABLED") ?? "false",
      "VITE_AUTH_GOOGLE_ENABLED",
    ),
  };
  const authDomain = readString(env, "VITE_FIREBASE_AUTH_DOMAIN") ?? `${projectId}.firebaseapp.com`;
  const storageBucket =
    readString(env, "VITE_FIREBASE_STORAGE_BUCKET") ?? `${projectId}.appspot.com`;

  assertOrgAdminCloudAuthConfiguration({
    apiBaseUrl,
    apiKey,
    appId,
    authDomain,
    authProviders,
    environment,
    projectId,
    storageBucket,
    useEmulators,
  });

  const config: OrgAdminEnvironmentConfig = {
    appCheck: resolveWebAppCheckConfiguration({
      environment,
      useEmulators,
      mode: readString(env, "VITE_APP_CHECK_CLIENT_MODE"),
      siteKey: readString(env, "VITE_APP_CHECK_RECAPTCHA_ENTERPRISE_SITE_KEY"),
    }),
    apiBaseUrl,
    authProviders,
    bannerLabel: environmentBannerLabel(environment),
    environment,
    featureFlags: {
      environmentDiagnostics: parseBooleanFlag(
        readString(env, "VITE_FEATURE_ENVIRONMENT_DIAGNOSTICS") ?? "false",
        "VITE_FEATURE_ENVIRONMENT_DIAGNOSTICS",
      ),
    },
    firebase: {
      apiKey,
      appId,
      authDomain,
      projectId,
      storageBucket,
      useEmulators,
    },
  };
  assertProductionWebEnvironment("org-admin", config);
  return config;
}

function assertOrgAdminCloudAuthConfiguration(input: {
  apiBaseUrl: string;
  apiKey: string;
  appId: string;
  authDomain: string;
  authProviders: OrgAdminEnvironmentConfig["authProviders"];
  environment: RuntimeEnvironment;
  projectId: string;
  storageBucket: string;
  useEmulators: boolean;
}): void {
  const target = orgAdminCloudAuthTargets[input.environment];

  if (!target || input.useEmulators || input.projectId !== target.projectId) {
    return;
  }

  assertNoPlaceholderConfigurationValue(input.apiKey, "VITE_FIREBASE_API_KEY");
  assertNoPlaceholderConfigurationValue(input.appId, "VITE_ORG_ADMIN_FIREBASE_APP_ID");
  assertNoPlaceholderConfigurationValue(input.apiBaseUrl, "VITE_API_BASE_URL");

  if (input.appId !== target.appId) {
    throw new EnvironmentConfigurationError(
      `${target.label} Org Admin builds must use VITE_ORG_ADMIN_FIREBASE_APP_ID=${target.appId}.`,
    );
  }

  if (input.authDomain !== target.authDomain) {
    throw new EnvironmentConfigurationError(
      `${target.label} Org Admin builds must use VITE_FIREBASE_AUTH_DOMAIN=${target.authDomain}.`,
    );
  }

  if (input.storageBucket !== target.storageBucket) {
    throw new EnvironmentConfigurationError(
      `${target.label} Org Admin builds must use VITE_FIREBASE_STORAGE_BUCKET=${target.storageBucket}.`,
    );
  }

  if (input.apiBaseUrl !== target.apiBaseUrl) {
    throw new EnvironmentConfigurationError(
      `${target.label} Org Admin builds must use VITE_API_BASE_URL=${target.apiBaseUrl}.`,
    );
  }

  if (input.authProviders.google !== target.authGoogleEnabled) {
    throw new EnvironmentConfigurationError(
      `${target.label} Org Admin builds must enable VITE_AUTH_GOOGLE_ENABLED.`,
    );
  }

  if (input.authProviders.apple !== target.authAppleEnabled) {
    throw new EnvironmentConfigurationError(
      `${target.label} Org Admin web builds must keep VITE_AUTH_APPLE_ENABLED=false until Firebase Apple code flow is configured.`,
    );
  }
}
